import { Notification } from '@components/prereg_validation/notification'
import { Box, Button, Paper, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useLocationContext } from '@providers/location/location_provider'
import { useSessionContext } from '@providers/session'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locale'
import { CustomError, DEFAULT_TIMEZONE } from 'components-ui/src'
import { Spacer } from 'components-ui/src/spacer/spacer'
import utcToZonedTime from 'date-fns-tz/esm/utcToZonedTime'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Hooks } from 'service-api'
import { ClientReportCardListService } from 'service-api/src/services/client_report_card_list.service'
import { captureException } from 'service-api/src/shared'
import { ReportCard } from 'service-api/src/types/reportCards'

type ReportCardState = {
  id: string
  publicUrl: string
  createdDate: string
  petName: string
}

export const ReportCards = () => {
  const sessionCtx = useSessionContext()
  const { location, isLoading: isLocationLoading } = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user?.lupId || ''
  const [reportCards, setReportCards] = useState<ReportCardState[]>([])
  const [isReportCardLoading, setIsReportCardLoading] = useState<boolean>(true)

  const { data, isFetching: isLUPLoading } = Hooks.useClientLocationUserProfile(locationName, lupId || '', [
    'locationPetProfiles',
  ])
  const isLoading = isLUPLoading || isLocationLoading || isReportCardLoading

  const petProfiles = useMemo(() => {
    if (data?.locationPetProfiles) {
      return data.locationPetProfiles
    }
    return []
  }, [data?.locationPetProfiles])

  useEffect(() => {
    const fetchReportCards = async () => {
      setIsReportCardLoading(true)
      try {
        const preparedPromises = petProfiles.map((pet) =>
          ClientReportCardListService.getReportCardListByPetId(locationName, pet.id || '')
        )
        const reportCards = await Promise.allSettled(preparedPromises)

        const mergedCards: ReportCardState[] = reportCards.flatMap((res) => {
          if (res.status === 'fulfilled' && Array.isArray(res.value)) {
            return res.value.map((card: ReportCard) => {
              return {
                id: card.id ?? '',
                publicUrl: card.publicUrl ?? '',
                createdDate: utcToZonedTime(
                  card.createdAtUtc || 0,
                  location?.timezone || DEFAULT_TIMEZONE
                ).toLocaleDateString('en-US'),
                petName: card?.lpp?.displayName || 'Unnamed Pet',
              }
            })
          }
          return []
        })
        // Sort report card by date
        const sortedCards = mergedCards.sort(
          (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        )
        setReportCards(sortedCards)
      } catch (error) {
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        captureException(new CustomError(buildErrorMessage(error), 'ClientReportCards', { cause: error }), {
          context: { locationName, lupId },
          tags: [{ key: 'errorType', value: 'report card fetch' }],
        })
      }
      setIsReportCardLoading(false)
    }

    if (petProfiles.length > 0 && !isLocationLoading) {
      fetchReportCards()
    }
  }, [locationName, petProfiles, lupId, location?.timezone, isLocationLoading])

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />
      <Typography variant="h2" sx={{ mb: 1 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.reportCards.title')}
      </Typography>
      <Typography variant="body1bold" color="text.secondary" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.reportCards.subtitle')}
      </Typography>
      <Spacer height={3} />
      {isLoading && <Skeleton variant="rectangular" width="100%" height={600} />}
      {!isLoading && reportCards.length === 0 && (
        <Paper sx={{ padding: 3 }}>
          <Typography variant="h5" color="text.secondary">
            {t('profile.reportCards.empty')}
          </Typography>
        </Paper>
      )}
      {!isLoading && reportCards.length > 0 && (
        <>
          {reportCards.map((card) => (
            <Paper
              key={card?.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                elevation: 2,
                padding: 1.5,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5">
                  {t('profile.reportCard.label')} - {card.createdDate}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {card.petName}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="medium"
                color="primary"
                onClick={() => window.open(card.publicUrl, '_blank')}
              >
                {t('profile.reportCards.button')}
              </Button>
            </Paper>
          ))}
        </>
      )}
    </Box>
  )
}
